import { Alert, Typography } from "@mui/material";
import { EmailIntakeSenderConflictKind } from "generated-graphql/graphql";

type Conflict = {
  tenantId: string;
  tenantName: string;
  value: string;
  enabled: boolean;
  kind: EmailIntakeSenderConflictKind;
};

/**
 * What else already claims this sender, and what saving anyway would cost.
 *
 * Two severities, because a collision that is switched off is not the same problem. A
 * disabled entry is worth showing — someone could switch it back on — but resolution
 * routes to the live entry, so calling it an active ambiguity would be false, and
 * crying wolf about a dormant one trains staff to ignore the callout.
 */
export function ConflictWarning({ conflicts }: { conflicts: readonly Conflict[] }) {
  if (conflicts.length === 0) return null;

  const live = conflicts.filter((conflict) => conflict.enabled);
  const dormant = conflicts.filter((conflict) => !conflict.enabled);

  return (
    <Alert severity={live.length > 0 ? "warning" : "info"}>
      {live.map((conflict) => (
        <Typography key={`${conflict.tenantId}-${conflict.value}`} variant="body2">
          {conflict.kind === EmailIntakeSenderConflictKind.SameValue ? (
            <>
              <strong>{conflict.tenantName}</strong> already allowlists{" "}
              <strong>{conflict.value}</strong>.
            </>
          ) : (
            <>
              <strong>{conflict.tenantName}</strong> allowlists <strong>{conflict.value}</strong>{" "}
              with subdomains, which overlaps this one.
            </>
          )}
        </Typography>
      ))}
      {dormant.map((conflict) => (
        <Typography key={`${conflict.tenantId}-${conflict.value}`} variant="body2">
          <strong>{conflict.tenantName}</strong> also has <strong>{conflict.value}</strong>,
          currently switched off.
        </Typography>
      ))}
      <Typography variant="body2" sx={{ mt: 1 }}>
        {live.length > 0
          ? "Saving this makes the affected senders ambiguous: their mail is held for a person to route instead of filing on its own, until one entry is removed."
          : "Nothing conflicts while that stays off — mail routes here. Re-enabling it would make these senders ambiguous, and their mail would be held for a person to route."}
      </Typography>
    </Alert>
  );
}
